/**
 * Cmarkdownpiechart Component
 * Renders a donut chart from mermaid pie syntax inside markdown
 * Thick rounded strokes with glow effect
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from '../Cmarkdownvisual';
import { pxToRem } from '@/theme/spacing';
import { PieChartBackgroundBloom } from './PieChartBackgroundBloom';
import { PieChartCompactPreview } from './PieChartCompactPreview';
import { PieChartContent } from './PieChartContent';
import { PIECHART_METRIC_UNIT } from './piechart-constants';
import { buildPieChartRenderModel } from './piechart-model';
import { parseMermaidPie } from './parse-mermaid-pie';
import type { ParsedPieChart } from './piechart-types';

const PIECHART_VALUE = "piechart";

const PIE_CHART_ID_UNSAFE_CHARS_REGEX = /[^a-zA-Z0-9_-]/g;
const PIE_CHART_ID_EDGE_DASHES_REGEX = /^-+|-+$/g;
const PIE_CHART_ID_HASH_OFFSET_BASIS = 2166136261;
const PIE_CHART_ID_HASH_PRIME = 16777619;

export interface CmarkdownpiechartProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

function sanitizePieChartIdSegment(value: string): string {
  const sanitized = value
    .replace(PIE_CHART_ID_UNSAFE_CHARS_REGEX, '-')
    .replace(PIE_CHART_ID_EDGE_DASHES_REGEX, '');

  return sanitized || PIECHART_VALUE;
}

function hashPieChartIdSource(value: string): string {
  let hash = PIE_CHART_ID_HASH_OFFSET_BASIS;
  for (let characterIndex = 0; characterIndex < value.length; characterIndex += 1) {
    hash ^= value.charCodeAt(characterIndex);
    hash = Math.imul(hash, PIE_CHART_ID_HASH_PRIME);
  }

  return (hash >>> 0).toString(36);
}

function createPieChartIdPrefix(input: {
  content: string;
  parsed: ParsedPieChart | null;
}): string {
  const semanticSource = input.parsed
    ? [
        input.parsed.title ?? PIECHART_VALUE,
        ...input.parsed.slices.map((slice) => `${slice.label}-${slice.value}`),
      ].join('-')
    : input.content;
  const semanticSegment = sanitizePieChartIdSegment(semanticSource).slice(0, 72);

  return `piechart-${semanticSegment}-${hashPieChartIdSource(input.content)}`;
}

export function Cmarkdownpiechart({
  content,
  className,
  style,
}: CmarkdownpiechartProps): ReactElement {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const parsed = useMemo(() => parseMermaidPie(content), [content]);
  const pieChartIdPrefix = useMemo(
    () => createPieChartIdPrefix({ content, parsed }),
    [content, parsed],
  );
  const renderModel = useMemo(
    () => parsed ? buildPieChartRenderModel(parsed) : null,
    [parsed],
  );

  if (!parsed || !renderModel) {
    return (
      <CmarkdownvisualError
        type={CmarkdownvisualType.Pie}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  return (
    <Cmarkdownvisual
      type={CmarkdownvisualType.Pie}
      title={parsed.title}
      contentStyle={{ padding: pxToRem(24), display: 'flex', flexDirection: 'column', gap: 16 }}
      className={className}
      style={style}

      compactPreview={<PieChartCompactPreview sectors={renderModel.compactSectors} />}
      metric={{ value: Math.round(renderModel.total), unit: PIECHART_METRIC_UNIT }}
    >
      <PieChartBackgroundBloom arc={renderModel.arcs[0]} />
      <PieChartContent
        hoveredIndex={hoveredIndex}
        onHoveredIndexChange={setHoveredIndex}
        pieChartIdPrefix={pieChartIdPrefix}
        renderModel={renderModel}
      />
    </Cmarkdownvisual>
  );
}
